/**
 * Pool Accounts — the list of configured pool accounts with their Zoom-mirrored usage (design §11
 * Phase 1, screen 2). FreshnessBadge is fed from GET /pool/status's `last_complete.finished_at` — the
 * last sweep that actually finished `complete` (never `last_run`, which may still be running or may
 * have partial_aborted) — reused verbatim, not re-derived (see components/portal/FreshnessBadge.tsx).
 *
 * rev 6 R5: the mirror-derived columns are Account Type / Seats (hosts_total) / Subscription Start /
 * Subscription End — a standalone (credential, R4) account has no ZPCD mirror row at all, so these four
 * render "—" via the same null-cell handling every other account already gets (nothing special-cased for
 * standalone).
 *
 * rev 7 (21/7, prod screenshot review): Account Type now reads the new `account_type` field (the real
 * plan label, e.g. "Education"/"Pro") instead of `plan_type` — see PoolAccount's own banner in
 * api/types.ts for why the two are not interchangeable and `plan_type` is not simply renamed.
 *
 * rev 8 (21/7, prod screenshot review): "Sync now" beside the Last Update chip — owner/admin only (a
 * pool-Viewer never sees it, gated client-side on usePoolAccess exactly like Settings/System/Audit; the
 * underlying POST is `_manage`-gated server-side too, so a client bug here would still 403). Enqueues an
 * out-of-cadence sweep run (useRunPoolSweep, features/pool/hooks.ts); a 409 (already running) is an
 * informational toast, never an error — the module's sweep is a singleton by design, this is expected.
 *
 * rev 17c (23/7, "Sync now" feedback, option A) — the button now tracks the enqueued run to an actual
 * observed completion instead of firing a single "started" toast and hoping: it enters a running state
 * (label swaps, spinner) and polls GET /pool/status for a NEW `last_complete.id`, THEN toasts + invalidates
 * (see useRunPoolSweep's own banner for the full state machine — running/complete/paused/timeout/
 * alreadyRunning/error). A static "auto-syncs every 5 minutes" caption sits beside the Last Update chip
 * so an operator who never touches this button still knows the screen isn't frozen between sweeps.
 *
 * rev 9 (21/7, principal feedback):
 *  • "Licensed" is renamed "Assigned" — same field (`licensed_members`, membership rows with
 *    user_type=2), the old name just described Zoom's OWN "Licensed" user-type token rather than what
 *    the column counts (assigned seats out of the pool), which read as a duplicate of the Pool Users
 *    License column.
 *  • NEW "Utilization" column = Assigned / Seats as an integer percent — em dash when Seats is null/0
 *    (never a false 0%), warn-toned past 100% (over-assigned relative to the mirrored seat count).
 *  • The row click is GONE; the Account Name cell is now the (only) navigation element into Pool Users
 *    filtered to that account — a real `<Link>` (anchor semantics, keyboard/right-click/cmd-click all
 *    work for free) rather than an onClick on the whole `<tr>`.
 *
 * rev 10 (22/7, principal feedback):
 *  • "Members" is GONE entirely — superseded by Assigned + the new Unassigned column below; nothing read
 *    it for anything the two didn't already cover (see PoolAccount.members's own banner in api/types.ts).
 *  • NEW "Unassigned" column = Seats (hosts_total) − Assigned (licensed_members), placed right after
 *    Assigned. The RAW difference — never clamped at 0 — so an oversold pool (more licenses assigned than
 *    the mirrored seat count) shows a visible negative number instead of a false 0 (data honesty, the same
 *    reasoning as Utilization's own warn tone past 100%); warn-toned when negative. Em dash when either
 *    half is unknown (no mirror row — a standalone account — or the license count hasn't synced).
 *  • Account Type: rev 7's belief that this field carries a clean label ("Pro"/"Education"/…) was WRONG —
 *    verified on prod 21/7 that it's the same raw base_type-style key as everywhere else (edu20 / yearly /
 *    zoe_yearly / …). Now routed through `tierOf()` (lib/planNames.ts) to bucket into the four tiers the
 *    principal actually wants (Pro/Business/Enterprise/Education) — see that function's own banner for the
 *    key-family rules and the real prod keys it was verified against.
 *  • New column order: Account Name · Account Number · Account Type · Seats · Subscription Start ·
 *    Subscription End · Assigned · Unassigned · Utilization.
 */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import { Button, EmptyState, SkeletonRows, Table, useNotifier, type Column } from "@/components/ui";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import { unassignedCount, utilizationPct } from "@/features/pool/poolAccountStats";
import {
  usePoolAccess,
  usePoolAccounts,
  usePoolStatus,
  useRunPoolSweep,
  type PoolSyncNowPhase,
} from "@/features/pool/hooks";
import { formatDate, formatInt } from "@/lib/format";
import { tierOf } from "@/lib/planNames";
import type { PoolAccount } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** "Sync now" — owner/admin only (see this file's rev 8 banner). A standalone component so the
 *  mutation/notify wiring doesn't clutter PoolAccountsPage's own render body.
 *
 *  rev 17c — one toast per SETTLED transition (complete/paused/timeout/alreadyRunning/error), ref-guarded
 *  the same way features/customer/SyncNowControl.tsx guards its own re-render-safe toast so a re-render
 *  never double-fires. `posting`/`running` are the transient in-flight phases — the button's own
 *  label+spinner communicate those, no toast for either. */
function SyncNowButton() {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const { phase, isBusy, run } = useRunPoolSweep();

  const lastNotified = useRef<PoolSyncNowPhase["phase"]>("idle");
  useEffect(() => {
    if (phase.phase === lastNotified.current) return;
    lastNotified.current = phase.phase;
    if (phase.phase === "complete") {
      notify({ tone: "ok", title: t("pool.accounts.syncComplete") });
    } else if (phase.phase === "paused") {
      notify({ tone: "info", title: t("pool.accounts.syncPaused") });
    } else if (phase.phase === "timeout") {
      notify({ tone: "info", title: t("pool.accounts.syncTimeout") });
    } else if (phase.phase === "alreadyRunning") {
      // 409 = a sweep is already running — expected, not a failure (see this file's rev 8 banner).
      notify({ tone: "info", title: t("pool.accounts.syncAlreadyRunning") });
    } else if (phase.phase === "error") {
      notify({ tone: "danger", title: errMessage(phase.error, t("state.errorTitle")) });
    }
  }, [phase, notify, t]);

  return (
    <Button size="sm" variant="secondary" loading={isBusy} onClick={run}>
      <RefreshCw className="size-4" aria-hidden />
      {phase.phase === "running"
        ? t("pool.accounts.syncRunning")
        : t("pool.accounts.syncNowButton")}
    </Button>
  );
}

export function PoolAccountsPage() {
  const { t } = useTranslation();
  const accountsQ = usePoolAccounts();
  const statusQ = usePoolStatus();
  const accessQ = usePoolAccess();
  const canManage = accessQ.data?.role === "owner" || accessQ.data?.role === "admin";

  const columns: Column<PoolAccount>[] = [
    {
      key: "name",
      header: t("pool.accounts.colName"),
      sortValue: (r) => r.account_name ?? "",
      // The ONLY navigation element into Pool Users, filtered to this account (rev 9 — the row click
      // it replaces). A real <Link> so anchor semantics (keyboard focus/activate, right-click, cmd/
      // ctrl-click to open in a new tab) all work without any extra wiring.
      cell: (r) => (
        <Link
          to={`/pool/users?account=${encodeURIComponent(r.account_id)}`}
          className="font-medium text-(--brand-strong) hover:underline"
        >
          {r.account_name ?? "—"}
        </Link>
      ),
    },
    {
      key: "number",
      header: t("pool.accounts.colNumber"),
      sortValue: (r) => r.account_number ?? "",
      cell: (r) => r.account_number ?? "—",
    },
    {
      key: "accountType",
      header: t("pool.accounts.colAccountType"),
      // rev 10 (22/7): account_type is a raw base_type-style key, not a display label — bucket it into
      // one of the four tiers via tierOf() (see this file's own rev 10 banner + tierOf's in planNames.ts).
      sortValue: (r) => tierOf(r.account_type) ?? "",
      cell: (r) => tierOf(r.account_type) ?? "—",
    },
    {
      key: "seats",
      header: t("pool.accounts.colSeats"),
      numeric: true,
      sortValue: (r) => r.hosts_total ?? -1,
      cell: (r) => (r.hosts_total != null ? formatInt(r.hosts_total) : "—"),
    },
    {
      key: "subscriptionStart",
      header: t("pool.accounts.colSubscriptionStart"),
      sortValue: (r) => r.subscription_start ?? "",
      cell: (r) => formatDate(r.subscription_start),
    },
    {
      key: "subscriptionEnd",
      header: t("pool.accounts.colSubscriptionEnd"),
      sortValue: (r) => r.subscription_end ?? "",
      cell: (r) => formatDate(r.subscription_end),
    },
    {
      key: "assignedMembers",
      header: t("pool.accounts.colAssignedMembers"),
      numeric: true,
      sortValue: (r) => r.licensed_members ?? -1,
      cell: (r) => (r.licensed_members != null ? formatInt(r.licensed_members) : "—"),
    },
    {
      key: "unassigned",
      header: t("pool.accounts.colUnassigned"),
      numeric: true,
      // Table's own sort already treats null as "sort last regardless of direction" — no `?? sentinel`
      // needed (and a `-1` sentinel would be actively wrong here: unlike every OTHER numeric column on
      // this table, Unassigned's real values CAN be negative, so `-1` could collide with a genuine
      // oversold count instead of meaning "unknown").
      sortValue: (r) => unassignedCount(r),
      cell: (r) => {
        const n = unassignedCount(r);
        if (n == null) return "—";
        return (
          <span className={n < 0 ? "font-semibold text-(--warn)" : undefined}>{formatInt(n)}</span>
        );
      },
    },
    {
      key: "utilization",
      header: t("pool.accounts.colUtilization"),
      numeric: true,
      sortValue: (r) => utilizationPct(r) ?? -1,
      cell: (r) => {
        const pct = utilizationPct(r);
        if (pct == null) return "—";
        return (
          <span className={pct > 100 ? "font-semibold text-(--warn)" : undefined}>{`${pct}%`}</span>
        );
      },
    },
  ];

  return (
    <section aria-labelledby="pool-accounts-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1
            id="pool-accounts-title"
            className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
          >
            {t("pool.accounts.title")}
          </h1>
          {!statusQ.isPending && !statusQ.isError && (
            <FreshnessBadge
              kind="mirror"
              timestamp={statusQ.data.last_complete?.finished_at ?? null}
            />
          )}
          {/* rev 17c — a static fact, not data-dependent, so it doesn't wait on statusQ: an operator who
              never touches "Sync now" should still know the screen isn't just frozen between sweeps. */}
          <span className="text-(length:--text-2xs) text-(--text-subtle)">
            {t("pool.accounts.autoSyncNote")}
          </span>
          {canManage && <SyncNowButton />}
        </div>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.accounts.subtitle")}</p>
      </header>

      {accountsQ.isPending ? (
        <SkeletonRows rows={6} />
      ) : accountsQ.isError ? (
        <PoolErrorState error={accountsQ.error} onRetry={() => void accountsQ.refetch()} />
      ) : (
        <Table
          columns={columns}
          rows={accountsQ.data.accounts}
          rowKey={(r) => r.account_id}
          caption={t("pool.accounts.title")}
          initialSort={{ key: "name", dir: "asc" }}
          initialPageSize={50}
          empty={
            <EmptyState title={t("pool.accounts.emptyTitle")} body={t("pool.accounts.emptyBody")} />
          }
        />
      )}
    </section>
  );
}
